import { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";

// Intervalo do heartbeat no backend (server:heartbeat). Mantê-los alinhados.
const HEARTBEAT_MS = 5000;
// Sem heartbeat por mais de ~2,5 intervalos → considera o tempo-real travado.
const STALE_AFTER_MS = HEARTBEAT_MS * 2.5;

/**
 * Monitora o WebSocket e estima o ATRASO DE RECEPÇÃO dos eventos (one-way,
 * server→client). O backend emite "server:heartbeat" com o seu relógio; o front
 * faz `Date.now() - serverTime` = atraso bruto.
 *
 * Como os relógios podem estar dessincronizados, descontamos o clock skew: o
 * MENOR atraso bruto observado vira a baseline (0 ms). Assim o número mostrado é
 * a degradação real (jitter/fila), não a diferença de relógio.
 *
 * Retorna { status, latency }:
 *  - status: "connected" | "connecting" | "disconnected"
 *  - latency: ms de atraso de recepção estimado (null se desconhecido)
 */
const useSocketStatus = () => {
  // Usa o MESMO socket que o app inteiro (AuthContext) — é nele que os eventos,
  // incluindo o server:heartbeat, chegam. (O socketManager.GetSocket() poderia
  // devolver outra instância e o hook nunca veria o heartbeat.)
  const { socket } = useContext(AuthContext);
  const [status, setStatus] = useState("connecting");
  const [latency, setLatency] = useState(null);
  const minRawRef = useRef(Infinity); // menor (serverTime→agora) = offset de skew
  const lastBeatRef = useRef(0);
  const staleTimer = useRef(null);

  useEffect(() => {
    if (!socket) return undefined;

    const onConnect = () => setStatus("connected");
    const onDisconnect = () => {
      setStatus("disconnected");
      setLatency(null);
    };

    const onHeartbeat = payload => {
      const serverTime = payload?.serverTime;
      if (typeof serverTime !== "number") return;
      lastBeatRef.current = Date.now();
      setStatus("connected");

      const raw = Date.now() - serverTime; // atraso bruto (inclui skew)
      if (raw < minRawRef.current) minRawRef.current = raw; // baseline = menor visto
      const delay = Math.max(0, raw - minRawRef.current); // desconta o skew
      setLatency(delay);
    };

    // O wrapper SocketWorker expõe só on/off/emit (não .io nem .connected).
    // O próprio heartbeat é o sinal de vida: se chega, está conectado.
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("server:heartbeat", onHeartbeat);

    // Vigia se o heartbeat parou de chegar (tempo-real travado): mostra há quanto
    // tempo está sem receber, crescendo, no client-side.
    staleTimer.current = setInterval(() => {
      if (!lastBeatRef.current) return;
      const since = Date.now() - lastBeatRef.current;
      if (since > STALE_AFTER_MS) setLatency(Math.round(since));
    }, HEARTBEAT_MS);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("server:heartbeat", onHeartbeat);
      clearInterval(staleTimer.current);
    };
  }, [socket]);

  return { status, latency };
};

export default useSocketStatus;
